// Candidates data hook — single source of truth for the public
// Candidates module. Fetches the DB-backed list from /api/candidates;
// the JSON-derived array from buildCandidates.ts stays as a build-
// time fallback so a 503 / cold start / DB blip never blanks the
// homepage section.

import useSWR from 'swr';
import type { CandidateRow } from '../db/types';
import {
  CANDIDATES as JSON_CANDIDATES,
  DISTRICTS as JSON_DISTRICTS,
  PARTIES as JSON_PARTIES,
  type Candidate,
  type District,
} from './buildCandidates';

interface ApiResponse {
  candidates: CandidateRow[];
  fetchedAt?: string;
}

const fetcher = async (url: string): Promise<ApiResponse> => {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ApiResponse;
};

const MAJOR_PARTY_ORDER = ['PL', 'PN', 'ADPD', 'AD', 'Momentum', 'Imperium', 'Independent', 'IND'];

function reshape(rows: CandidateRow[]): {
  candidates: Candidate[];
  districts: District[];
  parties: string[];
} {
  // One DB row → one Candidate. Multi-district candidates have
  // district + district_2 set; merge into a districts[] array.
  const candidates: Candidate[] = rows.map((r) => {
    const districts: District[] = [];
    if (r.district != null) districts.push(r.district);
    if (r.district_2 != null && r.district_2 !== r.district)
      districts.push(r.district_2);
    if (districts.length === 0) districts.push('TBC');
    return {
      name: r.full_name,
      party: r.party,
      districts,
    };
  });

  // Sort by surname, like buildCandidates does for the JSON fallback.
  candidates.sort((a, b) => {
    const sa = surname(a.name);
    const sb = surname(b.name);
    return (
      sa.localeCompare(sb, 'en', { sensitivity: 'base' }) ||
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
    );
  });

  const partySet = new Set(candidates.map((c) => c.party));
  const knownMajor = MAJOR_PARTY_ORDER.filter((p) => partySet.has(p));
  const others = [...partySet].filter((p) => !MAJOR_PARTY_ORDER.includes(p)).sort();
  const parties = [...knownMajor, ...others];

  const districtSet = new Set<District>();
  for (const c of candidates) for (const d of c.districts) districtSet.add(d);
  const numericDistricts = [...districtSet]
    .filter((d): d is number => typeof d === 'number')
    .sort((a, b) => a - b);
  const districts: District[] = districtSet.has('TBC')
    ? [...numericDistricts, 'TBC']
    : numericDistricts;

  return { candidates, districts, parties };
}

function surname(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1] ?? fullName;
}

export interface CandidatesData {
  candidates: Candidate[];
  districts: District[];
  parties: string[];
  /** True when the live API returned data; false while loading or
   *  after a fetch error (in which case the build-time JSON list is
   *  used). Useful for surfacing a "showing local fallback" indicator
   *  in dev, but the public page doesn't need to differentiate. */
  fromApi: boolean;
}

const FALLBACK_DATA: CandidatesData = {
  candidates: JSON_CANDIDATES,
  districts: JSON_DISTRICTS,
  parties: JSON_PARTIES,
  fromApi: false,
};

export function useCandidates(): CandidatesData {
  const { data, error } = useSWR<ApiResponse>('/api/candidates', fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 30_000,
    errorRetryCount: 2,
  });
  if (error || !data) return FALLBACK_DATA;
  if (data.candidates.length === 0) return FALLBACK_DATA;
  const reshaped = reshape(data.candidates);
  return {
    candidates: reshaped.candidates,
    districts: reshaped.districts,
    parties: reshaped.parties,
    fromApi: true,
  };
}
